import { useEffect, useRef } from 'react';
import { FieldValues, Path, UseFormGetValues, UseFormSetValue } from 'react-hook-form';

type FieldKeys = keyof FieldValues;

export const SIGN_UP_DATA_STORAGE_KEY = 'sign-up-data';

interface UseCacheEnteredData<T extends FieldValues> {
  getValues: UseFormGetValues<T>;
  setValue: UseFormSetValue<T>;
  triggerForSaveData: string | undefined;
  storedValues: FieldKeys[];
}

export const useCacheSignUpData = <T extends FieldValues>({
  triggerForSaveData,
  getValues,
  setValue,
  storedValues
}: UseCacheEnteredData<T>) => {
  const isFirstRender = useRef(true);

  useEffect(() => {
    const data = sessionStorage.getItem(SIGN_UP_DATA_STORAGE_KEY);
    if (!data) return;

    try {
      const valuesFromStore = JSON.parse(data);
      if (valuesFromStore && typeof valuesFromStore === 'object') {
        Object.entries(valuesFromStore as FieldValues).forEach(([key, value]) => {
          setValue(key as Path<T>, value);
        });
      }
    } catch (_) {
      console.error(`Invalid JSON in sessionStorage: ${SIGN_UP_DATA_STORAGE_KEY}`);
    }
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const neededValues = Object.fromEntries(
      Object.entries(getValues()).filter(
        ([key]) => storedValues.includes(key as Path<T>) && Boolean(getValues(key as Path<T>))
      )
    );
    sessionStorage.setItem(SIGN_UP_DATA_STORAGE_KEY, JSON.stringify(neededValues));
  }, [triggerForSaveData]);
};
